import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import DashboardShell from "@/components/DashboardShell";
import api from "@/lib/api";
import { fmtEUR, categoryLabel } from "@/lib/constants";
import { LayoutDashboard, Settings, Users, Briefcase, FileText, MessageCircle, Mail, X, MessagesSquare, Trash2, Pencil, Download } from "lucide-react";
import { toast } from "sonner";
import ProfilePhoto from "@/components/ProfilePhoto";
import ChatPanel from "@/components/ChatPanel";
import AdminProspection from "@/components/AdminProspection";
import AdminBilling from "@/components/AdminBilling";

const NAV = [
    { to: "/admin", label: "Tableau de bord", icon: LayoutDashboard, end: true },
    { to: "/admin/missions", label: "Missions", icon: Briefcase },
    { to: "/admin/candidats", label: "Candidats", icon: Users },
    { to: "/admin/devis", label: "Devis", icon: FileText },
    { to: "/admin/parametres", label: "Paramètres tarifs", icon: Settings },
];

// Left-sidebar path <-> internal tab-state mapping
const PATH_TO_TAB = {
    "/admin": "overview",
    "/admin/missions": "missions",
    "/admin/candidats": "candidates",
    "/admin/devis": "quotes",
    "/admin/parametres": "settings",
};
const TAB_TO_PATH = Object.fromEntries(
    Object.entries(PATH_TO_TAB).map(([p, t]) => [t, p])
);

export default function AdminDashboard() {
    const location = useLocation();
    const navigate = useNavigate();
    const [tab, setTabState] = useState(PATH_TO_TAB[location.pathname] || "overview");
    const [dash, setDash] = useState(null);
    const [missions, setMissions] = useState([]);
    const [candidates, setCandidates] = useState([]);
    const [quotes, setQuotes] = useState([]);
    const [contracts, setContracts] = useState([]);
    const [settings, setSettings] = useState(null);
    const [unreadSignups, setUnreadSignups] = useState(0);
    const [unreadMessages, setUnreadMessages] = useState(0);
    const [unreadChat, setUnreadChat] = useState(0);
    const [editingQuote, setEditingQuote] = useState(null);

    const refresh = async () => {
        const results = await Promise.allSettled([
            api.get("/admin/dashboard"),
            api.get("/admin/missions"),
            api.get("/admin/candidates"),
            api.get("/quotes"),
            api.get("/admin/settings"),
            api.get("/contracts"),
            api.get("/admin/recent-signups"),
            api.get("/admin/messages/unread-count"),
            api.get("/chat/unread-count"),
        ]);
        if (results[0].status === "fulfilled") setDash(results[0].value.data);
        if (results[1].status === "fulfilled") setMissions(results[1].value.data);
        if (results[2].status === "fulfilled") setCandidates(results[2].value.data);
        if (results[3].status === "fulfilled") setQuotes(results[3].value.data);
        if (results[4].status === "fulfilled") setSettings(results[4].value.data);
        if (results[5].status === "fulfilled") setContracts(results[5].value.data);
        if (results[6].status === "fulfilled") setUnreadSignups(results[6].value.data?.unread_count || 0);
        if (results[7].status === "fulfilled") setUnreadMessages(results[7].value.data?.count || 0);
        if (results[8].status === "fulfilled") setUnreadChat(results[8].value.data?.count || 0);
    };

    useEffect(() => {
        refresh();
        // Poll unread badge every 30s so the admin sees new signups even without refresh
        const iv = setInterval(async () => {
            try {
                const [r1, r2, r3] = await Promise.allSettled([
                    api.get("/admin/recent-signups"),
                    api.get("/admin/messages/unread-count"),
                    api.get("/chat/unread-count"),
                ]);
                if (r1.status === "fulfilled") setUnreadSignups(r1.value.data?.unread_count || 0);
                if (r2.status === "fulfilled") setUnreadMessages(r2.value.data?.count || 0);
                if (r3.status === "fulfilled") setUnreadChat(r3.value.data?.count || 0);
            } catch (_) {}
        }, 15000);
        return () => clearInterval(iv);
    }, []);

    // Auto-clear the red badge when the admin opens the Inscriptions tab
    useEffect(() => {
        if (tab === "signups" && unreadSignups > 0) {
            api.post("/admin/signups/mark-viewed")
                .then(() => setUnreadSignups(0))
                .catch(() => {});
        }
        if (tab === "messages" && unreadMessages > 0) {
            api.post("/admin/messages/mark-all-read")
                .then(() => setUnreadMessages(0))
                .catch(() => {});
        }
    }, [tab, unreadSignups, unreadMessages]);

    // Sync left-sidebar URL <-> internal tab state (fixes clicks on
    // "Missions / Candidats / Devis / Paramètres" that only changed the URL
    // without switching the visible section).
    useEffect(() => {
        const mapped = PATH_TO_TAB[location.pathname];
        if (mapped && mapped !== tab) setTabState(mapped);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);

    const setTab = (next) => {
        setTabState(next);
        const targetPath = TAB_TO_PATH[next];
        if (targetPath && targetPath !== location.pathname) {
            navigate(targetPath, { replace: true });
        }
    };

    return (
        <DashboardShell title="Administration" nav={NAV}>
            <div className="mb-8">
                <div className="overline mb-2">Administration</div>
                <h1 className="font-display text-3xl md:text-4xl">Pilotage de la plateforme.</h1>
            </div>

            <div className="flex gap-2 mb-6 flex-wrap">
                <TabBtn active={tab === "overview"} onClick={() => setTab("overview")} label="Aperçu" />
                <TabBtn active={tab === "missions"} onClick={() => setTab("missions")} label={`Missions (${missions.length})`} testId="admin-tab-missions" />
                <TabBtn active={tab === "candidates"} onClick={() => setTab("candidates")} label={`Candidats (${candidates.length})`} testId="admin-tab-candidates" />
                <TabBtn active={tab === "quotes"} onClick={() => setTab("quotes")} label={`Devis (${quotes.length})`} testId="admin-tab-quotes" />
                <TabBtn active={tab === "contracts"} onClick={() => setTab("contracts")} label={`Contrats (${contracts.length})`} testId="admin-tab-contracts" />
                <TabBtn
                    active={tab === "signups"}
                    onClick={() => setTab("signups")}
                    label={
                        <span className="inline-flex items-center gap-2">
                            Inscriptions
                            {unreadSignups > 0 && (
                                <span
                                    className="inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 rounded-full bg-red-600 text-white text-[11px] font-bold animate-pulse"
                                    data-testid="admin-signups-badge"
                                >
                                    {unreadSignups}
                                </span>
                            )}
                        </span>
                    }
                    testId="admin-tab-signups"
                />
                <TabBtn
                    active={tab === "messages"}
                    onClick={() => setTab("messages")}
                    label={
                        <span className="inline-flex items-center gap-2">
                            <Mail size={14} /> Messagerie
                            {unreadMessages > 0 && (
                                <span
                                    className="inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 rounded-full bg-red-600 text-white text-[11px] font-bold animate-pulse"
                                    data-testid="admin-messages-badge"
                                >
                                    {unreadMessages}
                                </span>
                            )}
                        </span>
                    }
                    testId="admin-tab-messages"
                />
                <TabBtn
                    active={tab === "chat"}
                    onClick={() => setTab("chat")}
                    label={
                        <span className="inline-flex items-center gap-2">
                            <MessagesSquare size={14} /> Chat candidats
                            {unreadChat > 0 && (
                                <span
                                    className="inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 rounded-full bg-red-600 text-white text-[11px] font-bold animate-pulse"
                                    data-testid="admin-chat-badge"
                                >
                                    {unreadChat}
                                </span>
                            )}
                        </span>
                    }
                    testId="admin-tab-chat"
                />
                <TabBtn active={tab === "evaluations"} onClick={() => setTab("evaluations")} label="Évaluations" testId="admin-tab-evaluations" />
                <TabBtn active={tab === "expiring"} onClick={() => setTab("expiring")} label="Docs qui expirent" testId="admin-tab-expiring" />
                <TabBtn active={tab === "settings"} onClick={() => setTab("settings")} label="Paramètres tarifs" testId="admin-tab-settings" />
                <TabBtn active={tab === "revenue"} onClick={() => setTab("revenue")} label="Revenus par métier" testId="admin-tab-revenue" />
                <TabBtn active={tab === "prospection"} onClick={() => setTab("prospection")} label="Prospection B2B" testId="admin-tab-prospection" />
                <TabBtn active={tab === "billing"} onClick={() => setTab("billing")} label="Facturation J+10" testId="admin-tab-billing" />
            </div>

            {tab === "overview" && (
                <div className="space-y-6">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <KPI label="Chiffre d'affaires TTC" value={fmtEUR(dash?.revenue_ttc ?? 0)} />
                        <KPI label="Commissions" value={fmtEUR(dash?.commissions ?? 0)} />
                        <KPI label="Missions actives" value={dash?.missions_active ?? "—"} />
                        <KPI label="Missions totales" value={dash?.missions_total ?? "—"} />
                        <KPI label="Devis créés" value={dash?.quotes_total ?? "—"} />
                        <KPI label="Devis acceptés" value={dash?.quotes_accepted ?? "—"} />
                        <KPI label="Candidats validés" value={`${dash?.candidates_validated ?? 0} / ${dash?.candidates_total ?? 0}`} />
                        <KPI label="En attente" value={dash?.candidates_pending ?? 0} />
                    </div>
                    <AdminResetLinkTool />
                </div>
            )}

            {tab === "missions" && (
                <section className="sd-card p-6">
                    <div className="overline mb-3">Toutes les missions</div>
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left text-xs text-slate-500 border-b border-slate-200">
                                <th className="py-2">Mission</th>
                                <th>Ville</th>
                                <th>Date</th>
                                <th>Statut</th>
                                <th>Candidats</th>
                            </tr>
                        </thead>
                        <tbody>
                            {missions.map((m) => (
                                <AdminMissionRow key={m.id} m={m} onChange={refresh} />
                            ))}
                            {missions.length === 0 && (
                                <tr>
                                    <td colSpan={5} className="py-6 text-center text-slate-400">
                                        Aucune mission publiée.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </section>
            )}

            {tab === "candidates" && (
                <section className="sd-card p-6">
                    <div className="overline mb-3">Candidats</div>
                    <div className="grid md:grid-cols-2 gap-3">
                        {candidates.map((c) => (
                            <CandidateAdminCard key={c.id} c={c} onChange={refresh} />
                        ))}
                        {candidates.length === 0 && (
                            <div className="py-6 text-center text-slate-400 md:col-span-2">
                                Aucun candidat pour l'instant.
                            </div>
                        )}
                    </div>
                </section>
            )}

            {tab === "quotes" && (
                <section className="sd-card p-6 overflow-x-auto">
                    <div className="overline mb-3">Devis</div>
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left text-xs text-slate-500 border-b border-slate-200">
                                <th className="py-2">N°</th>
                                <th>Client</th>
                                <th>Mission</th>
                                <th>TTC</th>
                                <th>Statut</th>
                                <th className="text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {quotes.map((q) => (
                                <tr key={q.id} className="border-b border-slate-100 hover:bg-slate-50/60 transition-colors">
                                    <td className="py-2 font-mono text-xs">{q.quote_number}</td>
                                    <td>{q.company_info?.company_name || "—"}</td>
                                    <td>{q.mission_input?.title}</td>
                                    <td>{fmtEUR(q.quote_result?.total_ttc)}</td>
                                    <td>
                                        <span className={`pill ${q.status === "paid" ? "pill-green" : q.status === "accepted" ? "pill-gold" : "pill-slate"}`}>
                                            {q.status}
                                        </span>
                                    </td>
                                    <td className="text-right">
                                        <div className="inline-flex items-center gap-1">
                                            <a
                                                href={`${process.env.REACT_APP_BACKEND_URL}/api/quotes/${q.id}/pdf`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                title="Télécharger le PDF"
                                                className="p-1.5 rounded hover:bg-slate-200 text-slate-500 hover:text-[#0B192C] transition-colors"
                                                data-testid={`admin-quote-pdf-${q.id}`}
                                            >
                                                <Download size={16} />
                                            </a>
                                            <button
                                                onClick={() => setEditingQuote(q)}
                                                title="Modifier"
                                                className="p-1.5 rounded hover:bg-slate-200 text-slate-500 hover:text-[#0B192C] transition-colors"
                                                data-testid={`admin-quote-edit-${q.id}`}
                                            >
                                                <Pencil size={16} />
                                            </button>
                                            <button
                                                onClick={async () => {
                                                    if (!window.confirm(`Supprimer définitivement le devis ${q.quote_number} ?\n\nLes missions déjà générées à partir de ce devis seront conservées mais délinkées.`)) return;
                                                    try {
                                                        await api.delete(`/admin/quotes/${q.id}`);
                                                        toast.success("Devis supprimé");
                                                        refresh();
                                                    } catch (err) {
                                                        toast.error(err?.response?.data?.detail || "Erreur de suppression");
                                                    }
                                                }}
                                                title="Supprimer"
                                                className="p-1.5 rounded hover:bg-red-100 text-slate-500 hover:text-red-600 transition-colors"
                                                data-testid={`admin-quote-delete-${q.id}`}
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {quotes.length === 0 && (
                                <tr>
                                    <td colSpan={6} className="py-6 text-center text-slate-400">
                                        Aucun devis.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </section>
            )}

            {editingQuote && (
                <QuoteEditModal
                    quote={editingQuote}
                    onClose={() => setEditingQuote(null)}
                    onSaved={() => {
                        setEditingQuote(null);
                        refresh();
                    }}
                />
            )}

            {tab === "settings" && settings && (
                <SettingsForm settings={settings} onSave={refresh} />
            )}

            {tab === "evaluations" && (
                <AdminEvaluations />
            )}

            {tab === "signups" && (
                <AdminRecentSignups />
            )}

            {tab === "expiring" && (
                <AdminExpiringDocs />
            )}

            {tab === "messages" && (
                <AdminMessages />
            )}

            {tab === "chat" && (
                <AdminChat onUnreadRefresh={(n) => setUnreadChat(n)} />
            )}

            {tab === "revenue" && (
                <AdminRevenueByCategory />
            )}

            {tab === "prospection" && (
                <AdminProspection />
            )}

            {tab === "billing" && (
                <AdminBilling />
            )}

            {tab === "contracts" && (
                <section className="sd-card p-6 overflow-x-auto">
                    <div className="overline mb-3">Contrats auto-entrepreneur</div>
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left text-xs text-slate-500 border-b border-slate-200">
                                <th className="py-2">N°</th>
                                <th>Mission</th>
                                <th>Candidat</th>
                                <th>Montant HT</th>
                                <th>Statut</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {contracts.map((c) => {
                                const CTLABEL = { pending_signature: "À signer", signed: "Signé", cancelled: "Annulé", blocked: "Bloqué" };
                                const cand = candidates.find((x) => x.user_id === c.candidate_user_id);
                                const candName = cand
                                    ? `${cand.first_name || ""} ${(cand.last_name || "").slice(0, 1)}.`
                                    : `${c.candidate_user_id?.slice(0, 8)}…`;
                                return (
                                    <tr key={c.id} className="border-b border-slate-100">
                                        <td className="py-2 font-mono text-xs">{c.contract_number}</td>
                                        <td>{c.mission_snapshot?.title}</td>
                                        <td className="text-xs">{candName}</td>
                                        <td>{fmtEUR(c.amount_ht)}</td>
                                        <td>
                                            <span className={`pill ${c.status === "signed" ? "pill-green" : "pill-gold"}`}>
                                                {CTLABEL[c.status] || c.status}
                                            </span>
                                        </td>
                                        <td>
                                            {c.status === "signed" && (
                                                <button
                                                    onClick={async () => {
                                                        try {
                                                            const resp = await api.get(`/contracts/${c.id}/pdf`, { responseType: "blob" });
                                                            const url = URL.createObjectURL(resp.data);
                                                            window.open(url, "_blank");
                                                            setTimeout(() => URL.revokeObjectURL(url), 30000);
                                                        } catch (_) {}
                                                    }}
                                                    className="text-xs text-[#0B192C] hover:text-[#F5A623] font-semibold"
                                                >
                                                    PDF
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                            {contracts.length === 0 && (
                                <tr><td colSpan={6} className="py-6 text-center text-slate-400">Aucun contrat.</td></tr>
                            )}
                        </tbody>
                    </table>
                </section>
            )}
        </DashboardShell>
    );
}

function TabBtn({ active, onClick, label, testId }) {
    return (
        <button
            onClick={onClick}
            data-testid={testId}
            className={`px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${
                active
                    ? "bg-[#0B192C] text-white border-[#0B192C]"
                    : "bg-white border-slate-200 hover:border-[#0B192C]/30"
            }`}
        >
            {label}
        </button>
    );
}

function KPI({ label, value }) {
    return (
        <div className="sd-card p-5">
            <div className="overline mb-2">{label}</div>
            <div className="kpi-value">{value}</div>
        </div>
    );
}

function AdminMissionRow({ m, onChange }) {
    const [apps, setApps] = useState([]);
    const [open, setOpen] = useState(false);
    useEffect(() => {
        if (open) {
            api.get(`/applications/mission/${m.id}`).then((r) => setApps(r.data)).catch(() => {});
        }
    }, [open, m.id]);
    const updateApp = async (id, status) => {
        try {
            await api.patch(`/applications/${id}`, { status });
            toast.success("Statut mis à jour");
            api.get(`/applications/mission/${m.id}`).then((r) => setApps(r.data));
            onChange();
        } catch {
            toast.error("Erreur");
        }
    };
    return (
        <>
            <tr className="border-b border-slate-100">
                <td className="py-3 font-semibold">
                    {m.title}
                    <div className="text-xs text-slate-500 font-normal">{categoryLabel(m.job_category)}</div>
                </td>
                <td>{m.city}</td>
                <td>{m.date}</td>
                <td>
                    <span className={`pill ${m.status === "open" ? "pill-gold" : "pill-green"}`}>
                        {m.status}
                    </span>
                </td>
                <td>
                    <button
                        onClick={() => setOpen((v) => !v)}
                        className="btn-ghost-navy text-xs"
                        data-testid={`admin-mission-toggle-${m.id}`}
                    >
                        {open ? "Masquer" : "Voir"}
                    </button>
                </td>
            </tr>
            {open && (
                <tr>
                    <td colSpan={5} className="bg-slate-50 p-4">
                        {apps.length === 0 && <div className="text-sm text-slate-400">Aucune candidature.</div>}
                        <div className="grid md:grid-cols-2 gap-2">
                            {apps.map((a) => (
                                <div key={a.id} className="bg-white rounded-lg p-3 border border-slate-200">
                                    <div className="flex justify-between mb-2 gap-2">
                                        <div className="flex items-center gap-2 min-w-0">
                                            {a.candidate?.user_id && (
                                                <ProfilePhoto candidateUserId={a.candidate.user_id} size={40} />
                                            )}
                                            <div className="min-w-0">
                                                <div className="font-semibold text-sm truncate">
                                                    {a.candidate?.first_name} {a.candidate?.last_name}
                                                </div>
                                                <div className="text-xs text-slate-500 truncate">
                                                    {a.candidate?.city} · {a.candidate?.experience_years} ans
                                                </div>
                                            </div>
                                        </div>
                                        <span className={`pill shrink-0 ${a.status === "selected" ? "pill-green" : "pill-slate"}`}>{a.status}</span>
                                    </div>
                                    <div className="flex gap-1">
                                        <button onClick={() => updateApp(a.id, "preselected")} className="pill pill-gold text-xs">Préselec.</button>
                                        <button onClick={() => updateApp(a.id, "selected")} className="pill pill-green text-xs">Sélec.</button>
                                        <button onClick={() => updateApp(a.id, "rejected")} className="pill pill-red text-xs">Refuser</button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </td>
                </tr>
            )}
        </>
    );
}

function CandidateAdminCard({ c, onChange }) {
    const [showDocs, setShowDocs] = useState(false);
    const [showMessage, setShowMessage] = useState(false);
    const [showNotes, setShowNotes] = useState(false);
    const [docs, setDocs] = useState([]);
    const [notes, setNotes] = useState([]);
    const [noteText, setNoteText] = useState("");
    const [msgSubject, setMsgSubject] = useState("");
    const [msgBody, setMsgBody] = useState("");
    const [busy, setBusy] = useState(false);
    const loadDocs = async () => {
        try {
            const r = await api.get(`/admin/candidates/${c.user_id}/documents`);
            setDocs(r.data);
        } catch (_) {}
    };
    const loadNotes = async () => {
        try {
            const r = await api.get(`/admin/candidates/${c.user_id}/notes`);
            setNotes(r.data);
        } catch (_) {}
    };
    useEffect(() => {
        if (showDocs) loadDocs();
    }, [showDocs]);
    useEffect(() => {
        if (showNotes) loadNotes();
    }, [showNotes]);
    const sendMessage = async () => {
        if (msgBody.trim().length < 3) { toast.error("Message trop court"); return; }
        setBusy(true);
        try {
            await api.post(`/admin/candidates/${c.user_id}/message`, {
                subject: msgSubject, message: msgBody,
            });
            toast.success("Message envoyé");
            setShowMessage(false); setMsgSubject(""); setMsgBody("");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Erreur");
        } finally { setBusy(false); }
    };
    const addNote = async () => {
        if (!noteText.trim()) return;
        try {
            await api.post(`/admin/candidates/${c.user_id}/notes`, { text: noteText });
            setNoteText("");
            await loadNotes();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Erreur");
        }
    };
    const update = async (status) => {
        try {
            await api.patch(`/admin/candidates/${c.id}`, { profile_status: status });
            toast.success("Mise à jour");
            onChange();
        } catch {
            toast.error("Erreur");
        }
    };
    const reviewDoc = async (docId, status) => {
        try {
            await api.patch(`/admin/documents/${docId}`, { status });
            toast.success("Document " + (status === "verified" ? "validé" : "refusé"));
            await loadDocs();
        } catch {
            toast.error("Erreur");
        }
    };
    const downloadDoc = async (d) => {
        try {
            const resp = await api.get(
                `/candidates/documents/${d.id}/download`,
                { responseType: "blob" }
            );
            const url = URL.createObjectURL(resp.data);
            window.open(url, "_blank");
            setTimeout(() => URL.revokeObjectURL(url), 30000);
        } catch {
            toast.error("Erreur");
        }
    };
    const deleteCandidate = async () => {
        if (!window.confirm(
            `⚠️ Suppression RGPD de ${c.first_name} ${c.last_name}\n\n` +
            "Cette action va effacer :\n" +
            "• Profil, photo, documents administratifs\n" +
            "• Toutes ses candidatures\n" +
            "• Évaluations émises\n\n" +
            "Les missions et factures liées sont conservées anonymisées (obligation légale comptable).\n\n" +
            "Continuer ?"
        )) return;
        try {
            await api.delete(`/admin/users/${c.user_id}`);
            toast.success("Compte supprimé (RGPD)");
            onChange();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Erreur suppression");
        }
    };
    return (
        <div className="sd-card p-4">
            <div className="flex justify-between items-start mb-2 gap-3">
                <div className="flex gap-3 items-center min-w-0">
                    <ProfilePhoto candidateUserId={c.user_id} size={52} />
                    <div className="min-w-0">
                        <div className="font-semibold truncate">
                            {c.first_name} {c.last_name}
                        </div>
                        <div className="text-xs text-slate-500 truncate">
                            {categoryLabel(c.primary_job) || "Métier non défini"} · {c.city || "—"}
                        </div>
                    </div>
                </div>
                <span className={`pill shrink-0 ${
                    c.profile_status === "validated" || c.profile_status === "premium" ? "pill-green" :
                    c.profile_status === "suspended" || c.profile_status === "refused" ? "pill-red" :
                    "pill-gold"
                }`}>
                    {c.profile_status}
                </span>
            </div>
            <div className="text-xs text-slate-500 mb-3">
                {c.experience_years || 0} ans d'expérience · {c.professional_status}
            </div>
            <div className="flex flex-wrap gap-1 mb-2">
                <button onClick={() => update("validated")} className="pill pill-green text-xs" data-testid={`admin-validate-${c.id}`}>Valider</button>
                <button onClick={() => update("refused")} className="pill pill-red text-xs" data-testid={`admin-refuse-${c.id}`}>Refuser</button>
                <button onClick={() => update("pending_verification")} className="pill pill-gold text-xs">En attente</button>
                <button onClick={() => update("suspended")} className="pill pill-red text-xs">Suspendre</button>
                <button
                    onClick={deleteCandidate}
                    className="pill text-xs bg-red-600 text-white hover:bg-red-700"
                    data-testid={`admin-delete-candidate-${c.id}`}
                >
                    🗑️ Supprimer
                </button>
                <button
                    onClick={() => setShowDocs((v) => !v)}
                    className="pill text-xs"
                    data-testid={`admin-docs-toggle-${c.id}`}
                >
                    {showDocs ? "Masquer" : "Documents"}
                </button>
                <button
                    onClick={() => setShowMessage((v) => !v)}
                    className="pill text-xs"
                    data-testid={`admin-message-toggle-${c.id}`}
                >
                    ✉️ Message
                </button>
                <button
                    onClick={() => setShowNotes((v) => !v)}
                    className="pill text-xs"
                    data-testid={`admin-notes-toggle-${c.id}`}
                >
                    📝 Notes
                </button>
            </div>

            {showMessage && (
                <div className="mt-3 border-t border-slate-100 pt-3 space-y-2 bg-amber-50 rounded-lg p-3">
                    <div className="text-xs font-semibold text-[#0B192C]">
                        Envoyer un message à {c.first_name} par e-mail
                    </div>
                    <input
                        value={msgSubject}
                        onChange={(e) => setMsgSubject(e.target.value)}
                        placeholder="Sujet (facultatif)"
                        maxLength={150}
                        className="w-full text-xs px-2 py-1.5 rounded border border-slate-200 focus:border-[#F5A623] outline-none"
                        data-testid={`admin-message-subject-${c.id}`}
                    />
                    <textarea
                        value={msgBody}
                        onChange={(e) => setMsgBody(e.target.value)}
                        placeholder="Écrivez votre message…"
                        rows={3}
                        maxLength={5000}
                        className="w-full text-xs px-2 py-1.5 rounded border border-slate-200 focus:border-[#F5A623] outline-none"
                        data-testid={`admin-message-body-${c.id}`}
                    />
                    <button
                        onClick={sendMessage}
                        disabled={busy || msgBody.trim().length < 3}
                        className="btn-gold text-xs disabled:opacity-50"
                        data-testid={`admin-message-send-${c.id}`}
                    >
                        {busy ? "Envoi…" : "Envoyer"}
                    </button>
                </div>
            )}

            {showNotes && (
                <div className="mt-3 border-t border-slate-100 pt-3 space-y-2 bg-slate-50 rounded-lg p-3">
                    <div className="text-xs font-semibold text-[#0B192C]">
                        Notes internes (visibles admin uniquement)
                    </div>
                    <div className="space-y-1.5 max-h-40 overflow-y-auto">
                        {notes.length === 0 && (
                            <div className="text-xs text-slate-400">Aucune note pour ce candidat.</div>
                        )}
                        {notes.slice().reverse().map((n) => (
                            <div key={n.id} className="bg-white rounded p-2 border border-slate-200">
                                <div className="text-[10px] text-slate-400">{n.created_at?.slice(0, 16).replace("T", " ")}</div>
                                <div className="text-xs whitespace-pre-wrap">{n.text}</div>
                            </div>
                        ))}
                    </div>
                    <div className="flex gap-2">
                        <input
                            value={noteText}
                            onChange={(e) => setNoteText(e.target.value)}
                            placeholder="Ajouter une note…"
                            maxLength={2000}
                            className="flex-1 text-xs px-2 py-1.5 rounded border border-slate-200 focus:border-[#F5A623] outline-none"
                            data-testid={`admin-note-input-${c.id}`}
                        />
                        <button
                            onClick={addNote}
                            disabled={!noteText.trim()}
                            className="btn-gold text-xs disabled:opacity-50"
                            data-testid={`admin-note-add-${c.id}`}
                        >
                            Ajouter
                        </button>
                    </div>
                </div>
            )}
            {showDocs && (
                <div className="mt-2 border-t border-slate-100 pt-3 space-y-2">
                    {docs.length === 0 && (
                        <div className="text-xs text-slate-400">Aucun document téléversé.</div>
                    )}
                    {docs.map((d) => (
                        <div key={d.id} className="bg-slate-50 rounded-lg px-3 py-2 flex items-center gap-2 flex-wrap">
                            <div className="min-w-0 flex-1">
                                <div className="text-xs font-semibold truncate">{d.label || d.doc_type}</div>
                                <div className="text-[10px] text-slate-500 truncate">
                                    {d.original_filename} · {(d.size / 1024).toFixed(0)} Ko
                                </div>
                            </div>
                            <span className={`pill text-[10px] ${
                                d.status === "verified" ? "pill-green" :
                                d.status === "rejected" ? "pill-red" : "pill-gold"
                            }`}>
                                {d.status}
                            </span>
                            <button onClick={() => downloadDoc(d)} className="text-[11px] text-[#0B192C] hover:text-[#F5A623]">Voir</button>
                            <button onClick={() => reviewDoc(d.id, "verified")} className="pill pill-green text-[10px]">✓</button>
                            <button onClick={() => reviewDoc(d.id, "rejected")} className="pill pill-red text-[10px]">✗</button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function SettingsForm({ settings, onSave }) {
    const [s, setS] = useState(settings);
    const [saving, setSaving] = useState(false);
    const [resetting, setResetting] = useState(false);
    const set = (k, v) => setS((x) => ({ ...x, [k]: v }));
    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await api.put("/admin/settings", s);
            toast.success("Paramètres enregistrés");
            onSave();
        } catch {
            toast.error("Erreur");
        } finally {
            setSaving(false);
        }
    };

    const resetIndependentRates = async () => {
        if (!window.confirm(
            "Remettre la grille des tarifs auto-entrepreneur aux valeurs par défaut ?\n\n" +
            "Toute personnalisation des tarifs sera écrasée.\n" +
            "Les commissions et autres paramètres ne sont pas touchés."
        )) return;
        setResetting(true);
        try {
            const r = await api.post("/admin/settings/reset-independent-rates");
            setS((x) => ({ ...x, category_rates_independent: r.data.category_rates_independent }));
            toast.success("Grille des tarifs indépendants réinitialisée");
            onSave();
        } catch {
            toast.error("Erreur lors de la réinitialisation");
        } finally {
            setResetting(false);
        }
    };

    const NumField = ({ k, label, step = 1 }) => (
        <div>
            <label className="block text-xs font-semibold mb-1 text-slate-500">{label}</label>
            <input
                type="number"
                step={step}
                value={s[k] ?? 0}
                onChange={(e) => set(k, parseFloat(e.target.value) || 0)}
                className="w-full px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm"
                data-testid={`setting-${k}`}
            />
        </div>
    );

    return (
        <form onSubmit={submit} className="sd-card p-6 space-y-6">
            <div>
                <div className="overline mb-3">Base tarifaire (auto-entrepreneur)</div>
                <div className="grid md:grid-cols-4 gap-3">
                    <NumField k="min_hourly_rate_independent" label="Minimum auto-ent. (€/h)" step={0.01} />
                    <NumField k="default_hourly_rate" label="Taux horaire indicatif (€)" step={0.5} />
                    <NumField k="min_billable_hours" label="Minimum facturation (h)" step={0.5} />
                </div>
                <p className="text-xs text-slate-500 mt-3">
                    ⓘ Renforteo travaille exclusivement avec des auto-entrepreneurs / indépendants immatriculés. Frais plateforme = 15 % côté entreprise (inclus dans le prix TTC) + 8 % prélevés sur les honoraires HT du professionnel (mentionnés dans le contrat).
                </p>
            </div>
            <div>
                <div className="overline mb-3">Frais & commissions</div>
                <div className="grid md:grid-cols-4 gap-3">
                    <NumField k="platform_margin_pct" label="Commission plateforme côté client (%)" step={0.5} />
                    <NumField k="candidate_commission_pct" label="Commission prestataire auto-ent. (%)" step={0.5} />
                    <NumField k="management_fee_pct" label="Frais de gestion (%)" step={0.5} />
                    <NumField k="insurance_fee_pct" label="Assurance (%)" step={0.5} />
                    <NumField k="recruitment_fee_flat" label="Frais recrutement (€)" step={5} />
                    <NumField k="stripe_fee_pct" label="Frais Stripe (%)" step={0.1} />
                    <NumField k="vat_pct" label="TVA (%)" step={0.5} />
                    <NumField k="deposit_pct" label="Acompte (%)" step={5} />
                </div>
                <p className="text-[11px] text-slate-500 mt-2 leading-relaxed">
                    ⓘ 15 % côté entreprise (inclus dans le prix TTC) + 8 % prélevés sur les honoraires HT de l'auto-entrepreneur (mentionné dans le contrat). Aucune déclaration sociale n'est à la charge du client.
                </p>
            </div>
            <div>
                <div className="overline mb-3">Majorations</div>
                <div className="grid md:grid-cols-5 gap-3">
                    <NumField k="night_uplift_pct" label="Nuit (%)" />
                    <NumField k="sunday_uplift_pct" label="Dimanche (%)" />
                    <NumField k="holiday_uplift_pct" label="Férié (%)" />
                    <NumField k="urgency_uplift_pct" label="Urgent (%)" />
                    <NumField k="very_urgency_uplift_pct" label="Très urgent (%)" />
                </div>
            </div>
            <div>
                <div className="overline mb-3">Frais annexes</div>
                <div className="grid md:grid-cols-3 gap-3">
                    <NumField k="km_rate" label="Frais kilométriques (€/km)" step={0.05} />
                    <NumField k="meal_flat" label="Repas (€)" step={0.5} />
                    <NumField k="lodging_flat" label="Hébergement (€/nuit)" step={5} />
                </div>
            </div>
            {/* Grille tarifs indépendants + bouton de reset */}
            <div className="rounded-xl border-2 border-dashed border-[#F5A623]/40 bg-[#F5A623]/5 p-5">
                <div className="flex justify-between items-start flex-wrap gap-3 mb-3">
                    <div>
                        <div className="overline text-[#F5A623]">Grille — Tarifs indépendants</div>
                        <div className="text-sm text-slate-600 mt-1 max-w-xl">
                            Tarifs versés au professionnel indépendant, par métier. Les frais plateforme (15 %) sont calculés en plus.
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={resetIndependentRates}
                        disabled={resetting}
                        className="btn-ghost-navy text-xs disabled:opacity-50"
                        data-testid="reset-independent-rates-btn"
                    >
                        {resetting ? "Réinitialisation…" : "↺ Remettre la grille par défaut"}
                    </button>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {Object.entries(s.category_rates_independent || {})
                        .sort(([a], [b]) => a.localeCompare(b))
                        .map(([key, val]) => (
                            <div
                                key={key}
                                className="flex justify-between items-center bg-white rounded-lg border border-slate-200 px-3 py-2"
                                data-testid={`rate-independent-${key}`}
                            >
                                <span className="text-xs text-slate-600 capitalize">
                                    {key.replace(/_/g, " ")}
                                </span>
                                <span className="text-sm font-semibold text-[#0B192C]">
                                    {Number(val).toFixed(2)} €/h
                                </span>
                            </div>
                        ))}
                </div>
                <p className="text-[11px] text-slate-500 mt-3 leading-relaxed">
                    ⓘ Après un « Remettre la grille par défaut », les tarifs sont ceux définis dans le code (accueil 16 €/h, cuisinier 17 €/h, chef de cuisine 26 €/h, sécurité 18 €/h, etc.). Vos autres paramètres ne sont pas modifiés.
                </p>
            </div>
            <button className="btn-gold w-full" disabled={saving} data-testid="settings-save-btn">
                {saving ? "Enregistrement..." : "Enregistrer les paramètres"}
            </button>
        </form>
    );
}

function AdminEvaluations() {
    const [evals, setEvals] = useState([]);
    const [candidates, setCandidates] = useState({}); // user_id -> profile
    const [missions, setMissions] = useState({}); // id -> mission
    const STATUS_FR = {
        incomplete: "Incomplet",
        pending_verification: "En vérification",
        docs_needed: "Documents requis",
        interview: "Entretien",
        validated: "Validé",
        premium: "Premium",
        suspended: "Suspendu",
        refused: "Refusé",
        deleted: "Supprimé",
    };
    useEffect(() => {
        (async () => {
            try {
                const [e, c, m] = await Promise.all([
                    api.get("/admin/evaluations"),
                    api.get("/admin/candidates"),
                    api.get("/admin/missions"),
                ]);
                setEvals(e.data);
                setCandidates(Object.fromEntries(c.data.map((p) => [p.user_id, p])));
                setMissions(Object.fromEntries(m.data.map((mm) => [mm.id, mm])));
            } catch (_) {}
        })();
    }, []);

    // Compute per-candidate averages
    const byCandidate = {};
    evals.forEach((ev) => {
        (byCandidate[ev.candidate_id] = byCandidate[ev.candidate_id] || []).push(ev);
    });
    const summary = Object.entries(byCandidate).map(([cid, list]) => {
        const avg = list.reduce((s, e) => s + e.average, 0) / list.length;
        const recPct = Math.round(list.filter((e) => e.would_recommend).length / list.length * 100);
        return {
            candidate_id: cid,
            count: list.length,
            avg: Math.round(avg * 100) / 100,
            recommend_pct: recPct,
        };
    }).sort((a, b) => b.count - a.count);

    if (evals.length === 0) {
        return (
            <section className="sd-card p-6 text-sm text-slate-400 text-center">
                Aucune évaluation reçue pour l'instant.
            </section>
        );
    }

    return (
        <div className="space-y-6" data-testid="admin-evaluations">
            <section className="sd-card p-6 overflow-x-auto">
                <div className="overline mb-3">Notes moyennes par candidat</div>
                <table className="w-full text-sm">
                    <thead>
                        <tr className="text-left text-xs text-slate-500 border-b border-slate-200">
                            <th className="py-2">Candidat</th>
                            <th>Avis</th>
                            <th>Moyenne /5</th>
                            <th>Recommandations</th>
                            <th>Statut profil</th>
                        </tr>
                    </thead>
                    <tbody>
                        {summary.map((s) => {
                            const c = candidates[s.candidate_id] || {};
                            const suspended = c.profile_status === "suspended";
                            return (
                                <tr key={s.candidate_id} className="border-b border-slate-100">
                                    <td className="py-3 font-semibold">
                                        {c.first_name} {c.last_name}
                                    </td>
                                    <td>{s.count}</td>
                                    <td>
                                        <span className={`font-display font-bold ${s.avg < 2 ? "text-red-600" : s.avg < 3.5 ? "text-orange-500" : "text-green-600"}`}>
                                            {s.avg.toFixed(2)} ★
                                        </span>
                                    </td>
                                    <td>{s.recommend_pct}%</td>
                                    <td>
                                        {suspended ? (
                                            <span className="pill pill-red">Suspendu</span>
                                        ) : (
                                            <span className="pill pill-slate">{STATUS_FR[c.profile_status] || c.profile_status || "—"}</span>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </section>

            <section className="sd-card p-6">
                <div className="overline mb-3">Détail des évaluations récentes</div>
                <div className="divide-y divide-slate-100">
                    {evals.map((ev) => {
                        const c = candidates[ev.candidate_id] || {};
                        const m = missions[ev.mission_id] || {};
                        return (
                            <div key={ev.id} className="py-4" data-testid={`admin-eval-${ev.id}`}>
                                <div className="flex justify-between items-start gap-3 flex-wrap">
                                    <div>
                                        <div className="font-semibold text-sm">
                                            {c.first_name} {c.last_name}
                                            <span className="text-slate-400 font-normal">
                                                {m.title ? ` · ${m.title}${m.date ? ` — ${m.date}` : ""}` : ` · Mission ${ev.mission_id.slice(0, 8)}`}
                                            </span>
                                        </div>
                                        <div className="text-xs text-slate-500 mt-1">
                                            Ponctualité {ev.punctuality}/5 · Professionnalisme {ev.professionalism}/5 · Tenue {ev.appearance}/5 · Savoir-être {ev.attitude}/5
                                        </div>
                                        {ev.comment && (
                                            <div className="mt-2 text-xs italic text-slate-600 bg-slate-50 rounded p-2 border border-slate-200">
                                                « {ev.comment} »
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex flex-col items-end shrink-0">
                                        <span className={`font-display font-bold text-lg ${ev.average < 2 ? "text-red-600" : ev.average < 3.5 ? "text-orange-500" : "text-green-600"}`}>
                                            {ev.average.toFixed(2)} ★
                                        </span>
                                        <span className={`pill text-[10px] mt-1 ${ev.would_recommend ? "pill-green" : "pill-red"}`}>
                                            {ev.would_recommend ? "Recommandé" : "Non recommandé"}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </section>
        </div>
    );
}


function AdminResetLinkTool() {
    // Fallback admin tool used when the email provider is down (Resend 401,
    // DNS issues, user marked us as spam…). The admin types a user email,
    // hits the button and gets a fresh single-use reset link they can share
    // via any channel (WhatsApp, SMS, phone…).
    const [email, setEmail] = useState("");
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState("");

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        setError("");
        setResult(null);
        try {
            const { data } = await api.post("/admin/users/reset-link", { email: email.trim() });
            setResult(data);
        } catch (err) {
            setError(err?.response?.data?.detail || "Erreur lors de la génération du lien.");
        } finally {
            setBusy(false);
        }
    };

    const copy = async () => {
        if (!result?.reset_url) return;
        try {
            await navigator.clipboard.writeText(result.reset_url);
            toast.success("Lien copié dans le presse-papiers");
        } catch {
            toast.error("Impossible de copier — sélectionnez et copiez manuellement.");
        }
    };

    const reset = () => {
        setEmail("");
        setResult(null);
        setError("");
    };

    return (
        <section
            className="sd-card p-6 border-l-4 border-[#F5A623]"
            data-testid="admin-reset-link-tool"
        >
            <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
                <div>
                    <div className="overline text-[#F5A623]">Outil de dépannage</div>
                    <h3 className="font-display text-xl">Générer un lien de réinitialisation de mot de passe</h3>
                    <p className="text-xs text-slate-500 mt-1 max-w-lg leading-relaxed">
                        À utiliser lorsque le mail « mot de passe oublié » ne parvient
                        pas à l'utilisateur (provider e-mail indisponible, spam, adresse
                        obsolète…). Le lien est valable 1&nbsp;heure, à usage unique.
                    </p>
                </div>
            </div>

            {!result ? (
                <form onSubmit={submit} className="flex gap-2 flex-wrap items-end">
                    <div className="flex-1 min-w-[260px]">
                        <label className="block text-xs font-semibold text-slate-500 mb-1">
                            E-mail de l'utilisateur
                        </label>
                        <input
                            type="email"
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="ex : hichemsai1994@gmail.com"
                            className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus-ring bg-white text-sm"
                            data-testid="admin-reset-link-email-input"
                        />
                    </div>
                    <button
                        type="submit"
                        disabled={busy}
                        className="btn-gold text-sm disabled:opacity-50"
                        data-testid="admin-reset-link-submit"
                    >
                        {busy ? "Génération…" : "Générer le lien"}
                    </button>
                </form>
            ) : (
                <div className="space-y-3" data-testid="admin-reset-link-result">
                    <div className="text-sm">
                        <b>{result.user?.full_name || "Utilisateur"}</b>{" "}
                        <span className="text-slate-500">({result.user?.email} · {result.user?.role})</span>
                    </div>
                    <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 font-mono text-xs break-all">
                        {result.reset_url}
                    </div>
                    <div className="flex gap-2 flex-wrap">
                        <button
                            onClick={copy}
                            className="btn-gold text-sm"
                            data-testid="admin-reset-link-copy"
                        >
                            Copier le lien
                        </button>
                        <button
                            onClick={reset}
                            className="btn-ghost-navy text-sm"
                            data-testid="admin-reset-link-reset"
                        >
                            Générer un autre lien
                        </button>
                    </div>
                    <p className="text-[11px] text-slate-500 leading-relaxed">
                        Lien à usage unique · expire dans {result.ttl_minutes}&nbsp;min.
                        Envoyez-le à l'utilisateur par WhatsApp, SMS ou téléphone. Toute
                        demande précédente non utilisée a été invalidée.
                    </p>
                </div>
            )}

            {error && (
                <div
                    className="mt-3 rounded-xl border-l-4 border-red-500 bg-red-50 p-3 text-sm text-red-800"
                    data-testid="admin-reset-link-error"
                >
                    {error}
                </div>
            )}
        </section>
    );
}


function AdminRecentSignups() {
    const [data, setData] = useState({ count_24h: 0, users: [] });
    const refresh = () => {
        api.get("/admin/recent-signups")
            .then((r) => setData(r.data))
            .catch(() => setData({ count_24h: 0, users: [] }));
    };
    useEffect(() => { refresh(); }, []);
    const deleteUser = async (u) => {
        if (!window.confirm(
            `⚠️ Suppression RGPD du compte ${u.full_name || u.email}\n\n` +
            "Toutes les données personnelles seront effacées. Les factures liées sont conservées anonymisées.\n\n" +
            "Continuer ?"
        )) return;
        try {
            await api.delete(`/admin/users/${u.id}`);
            toast.success("Compte supprimé (RGPD)");
            refresh();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Erreur suppression");
        }
    };
    const fmt = (iso) => {
        if (!iso) return "—";
        try {
            const d = new Date(iso);
            return d.toLocaleString("fr-FR", {
                day: "2-digit", month: "2-digit", year: "numeric",
                hour: "2-digit", minute: "2-digit",
            });
        } catch (_) { return iso.slice(0, 16); }
    };
    return (
        <div className="space-y-6" data-testid="admin-signups">
            <section className="sd-card p-6 flex justify-between items-center flex-wrap gap-4 border-l-4 border-[#F5A623]">
                <div>
                    <div className="overline text-[#F5A623]">Dernières 24 h</div>
                    <div className="font-display text-3xl">
                        {data.count_24h} nouvelle{data.count_24h > 1 ? "s" : ""} inscription{data.count_24h > 1 ? "s" : ""}
                    </div>
                </div>
                <div className="text-xs text-slate-500 max-w-xs">
                    Un e-mail est envoyé automatiquement à l'administrateur à chaque
                    nouvelle inscription (candidat ou client).
                </div>
            </section>

            <section className="sd-card p-6 overflow-x-auto">
                <div className="overline mb-3">50 dernières inscriptions</div>
                {data.users.length === 0 ? (
                    <div className="text-sm text-slate-400 text-center py-6">
                        Aucune inscription pour l'instant.
                    </div>
                ) : (
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left text-xs text-slate-500 border-b border-slate-200">
                                <th className="py-2">Rôle</th>
                                <th>Nom</th>
                                <th>E-mail</th>
                                <th>Société</th>
                                <th>Téléphone</th>
                                <th>Inscrit le</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.users.map((u) => (
                                <tr key={u.id} className="border-b border-slate-100" data-testid={`signup-row-${u.id}`}>
                                    <td className="py-3">
                                        <span className={`pill text-[10px] ${u.role === "candidate" ? "pill-slate" : "pill-gold"}`}>
                                            {u.role === "candidate" ? "Candidat" : "Client"}
                                        </span>
                                    </td>
                                    <td className="font-semibold">{u.full_name || "—"}</td>
                                    <td className="text-slate-600">{u.email}</td>
                                    <td className="text-slate-500">{u.company_name || "—"}</td>
                                    <td className="text-slate-500">{u.phone || "—"}</td>
                                    <td className="text-xs text-slate-500">{fmt(u.created_at)}</td>
                                    <td>
                                        <button
                                            onClick={() => deleteUser(u)}
                                            className="text-red-600 hover:text-red-800 text-xs font-semibold"
                                            data-testid={`signup-delete-${u.id}`}
                                        >
                                            🗑️
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </section>
        </div>
    );
}


function AdminExpiringDocs() {
    const [items, setItems] = useState([]);
    const [busy, setBusy] = useState(false);
    const refresh = () => {
        api.get("/admin/documents/expiring")
            .then((r) => setItems(r.data || []))
            .catch(() => setItems([]));
    };
    useEffect(() => { refresh(); }, []);
    const triggerCheck = async () => {
        setBusy(true);
        try {
            const r = await api.post("/admin/documents/run-expiry-check");
            toast.success(`Contrôle lancé — ${r.data.reminders_sent} rappel${r.data.reminders_sent > 1 ? "s" : ""} envoyé${r.data.reminders_sent > 1 ? "s" : ""}`);
            refresh();
        } catch (e) {
            toast.error("Erreur");
        } finally { setBusy(false); }
    };
    return (
        <div className="space-y-6" data-testid="admin-expiring">
            <section className="sd-card p-6 flex justify-between items-center flex-wrap gap-4 border-l-4 border-amber-500">
                <div>
                    <div className="overline text-amber-600">Documents à surveiller</div>
                    <div className="font-display text-2xl">
                        {items.length} document{items.length > 1 ? "s" : ""} arrive{items.length > 1 ? "nt" : ""} à expiration ({"<"}60 j)
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                        Une alerte e-mail automatique est envoyée à J-30, J-14, J-7 et J-1.
                        Vérification toutes les 6 h.
                    </div>
                </div>
                <button
                    onClick={triggerCheck}
                    disabled={busy}
                    className="btn-ghost-navy text-sm disabled:opacity-50"
                    data-testid="admin-run-expiry-check"
                >
                    {busy ? "Envoi…" : "Lancer le contrôle maintenant"}
                </button>
            </section>

            <section className="sd-card p-6 overflow-x-auto">
                {items.length === 0 ? (
                    <div className="text-sm text-slate-400 text-center py-6">
                        Tous les documents sont à jour ✅
                    </div>
                ) : (
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left text-xs text-slate-500 border-b border-slate-200">
                                <th className="py-2">Candidat</th>
                                <th>Document</th>
                                <th>Expire le</th>
                                <th>Jours restants</th>
                                <th>Rappels envoyés</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((d) => {
                                const critical = d.days_left <= 7;
                                const expired = d.days_left <= 0;
                                return (
                                    <tr key={d.id} className="border-b border-slate-100" data-testid={`expiring-row-${d.id}`}>
                                        <td className="py-3 font-semibold">
                                            {d.candidate?.first_name} {d.candidate?.last_name}
                                        </td>
                                        <td>{d.label || d.doc_type}</td>
                                        <td className="text-slate-600">{d.valid_until}</td>
                                        <td>
                                            <span className={`pill text-[11px] ${expired ? "pill-red" : critical ? "pill-gold" : "pill-slate"}`}>
                                                {expired ? "Expiré" : `J-${d.days_left}`}
                                            </span>
                                        </td>
                                        <td className="text-xs text-slate-500">
                                            {(d.expiry_reminders_sent || []).map((t) => `J-${t}`).join(", ") || "—"}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </section>
        </div>
    );
}



function AdminMessages() {
    const [msgs, setMsgs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [reply, setReply] = useState(null);
    const [replyBody, setReplyBody] = useState("");
    const [replySubj, setReplySubj] = useState("");
    const [sending, setSending] = useState(false);

    const refresh = async () => {
        setLoading(true);
        try {
            const r = await api.get("/admin/messages");
            setMsgs(r.data || []);
        } catch (_) {
            setMsgs([]);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { refresh(); }, []);

    const openReply = (m) => {
        setReply(m);
        setReplySubj(m.subject ? `Re: ${m.subject}` : "");
        setReplyBody("");
    };

    const sendReply = async () => {
        if (replyBody.trim().length < 3) { toast.error("Message trop court"); return; }
        setSending(true);
        try {
            await api.post(`/admin/candidates/${reply.from_user_id}/message`, {
                subject: replySubj, message: replyBody,
            });
            toast.success("Réponse envoyée");
            setReply(null);
            setReplyBody("");
            setReplySubj("");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Erreur d'envoi");
        } finally {
            setSending(false);
        }
    };

    if (loading) {
        return <div className="sd-card p-6 text-sm text-slate-400">Chargement…</div>;
    }
    return (
        <section className="sd-card p-6" data-testid="admin-messages-panel">
            <div className="flex justify-between items-center mb-4 flex-wrap gap-3">
                <div>
                    <div className="overline">Messagerie</div>
                    <h2 className="font-display text-xl mt-1">Messages reçus des candidats</h2>
                    <p className="text-xs text-slate-500 mt-1">
                        Tous les messages envoyés depuis « Contacter Renforteo » et « Contactez-nous » (site public) — archivés ici en plus de l'e-mail admin.
                    </p>
                </div>
                <button onClick={refresh} className="btn-ghost-navy text-sm" data-testid="admin-messages-refresh">
                    Rafraîchir
                </button>
            </div>
            {msgs.length === 0 && (
                <div className="text-center text-slate-400 text-sm py-10" data-testid="admin-messages-empty">
                    Aucun message reçu pour l'instant.
                </div>
            )}
            <div className="divide-y divide-slate-100">
                {msgs.map((m) => (
                    <div key={m.id} className="py-4 grid md:grid-cols-[1fr_auto] gap-3" data-testid={`admin-message-row-${m.id}`}>
                        <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                                <span className={`pill ${m.read ? "pill-slate" : "pill-red"}`}>
                                    {m.read ? "Lu" : "Nouveau"}
                                </span>
                                {m.from_role === "public" && (
                                    <span className="pill pill-gold text-[10px]">Visiteur</span>
                                )}
                                <span className="font-semibold text-sm">{m.from_name || "Candidat"}</span>
                                <span className="text-xs text-slate-500">&lt;{m.from_email || "—"}&gt;</span>
                                {m.from_phone && (
                                    <span className="text-xs text-slate-500">· 📞 {m.from_phone}</span>
                                )}
                            </div>
                            <div className="text-xs text-slate-500 mt-1">
                                {new Date(m.created_at).toLocaleString("fr-FR")}
                                {m.subject && <> · <b>{m.subject}</b></>}
                            </div>
                            <div className="text-sm text-slate-700 mt-2 whitespace-pre-wrap bg-slate-50 rounded-lg p-3 border border-slate-100">
                                {m.message}
                            </div>
                        </div>
                        <div className="flex md:flex-col gap-2 justify-end items-start">
                            {m.from_user_id && (
                                <button
                                    onClick={() => openReply(m)}
                                    className="btn-gold text-xs"
                                    data-testid={`admin-message-reply-${m.id}`}
                                >
                                    Répondre
                                </button>
                            )}
                            <a
                                href={`mailto:${m.from_email}?subject=Re: ${encodeURIComponent(m.subject || "")}`}
                                className="btn-ghost-navy text-xs text-center"
                            >
                                Répondre par e-mail
                            </a>
                        </div>
                    </div>
                ))}
            </div>

            {reply && (
                <div
                    className="fixed inset-0 z-50 bg-[#0B192C]/70 backdrop-blur-sm flex items-center justify-center p-4"
                    onClick={() => setReply(null)}
                    data-testid="admin-reply-modal"
                >
                    <div
                        className="bg-white rounded-2xl max-w-lg w-full shadow-2xl overflow-hidden"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex justify-between items-start p-6 border-b border-slate-100">
                            <div>
                                <div className="overline text-[#F5A623]">Réponse</div>
                                <h3 className="font-display text-lg mt-1">À : {reply.from_name}</h3>
                                <p className="text-xs text-slate-500 mt-1">{reply.from_email}</p>
                            </div>
                            <button onClick={() => setReply(null)} className="text-slate-400 hover:text-slate-600">
                                <X size={18} />
                            </button>
                        </div>
                        <div className="p-6 space-y-4">
                            <div>
                                <label className="text-sm font-semibold block mb-1">Sujet</label>
                                <input
                                    value={replySubj}
                                    onChange={(e) => setReplySubj(e.target.value)}
                                    maxLength={150}
                                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                                    data-testid="admin-reply-subject"
                                />
                            </div>
                            <div>
                                <label className="text-sm font-semibold block mb-1">Message</label>
                                <textarea
                                    value={replyBody}
                                    onChange={(e) => setReplyBody(e.target.value)}
                                    rows={6}
                                    maxLength={5000}
                                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                                    data-testid="admin-reply-body"
                                />
                                <div className="text-[10px] text-slate-400 text-right mt-1">{replyBody.length}/5000</div>
                            </div>
                        </div>
                        <div className="p-4 border-t border-slate-100 flex justify-end gap-2 bg-slate-50">
                            <button onClick={() => setReply(null)} className="btn-ghost-navy text-sm">Annuler</button>
                            <button
                                onClick={sendReply}
                                disabled={sending || replyBody.trim().length < 3}
                                className="btn-gold text-sm disabled:opacity-50"
                                data-testid="admin-reply-send"
                            >
                                {sending ? "Envoi…" : "Envoyer"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
}


function AdminChat({ onUnreadRefresh }) {
    const [threads, setThreads] = useState([]);
    const [selected, setSelected] = useState(null);
    const [loading, setLoading] = useState(true);

    const refresh = async () => {
        setLoading(true);
        try {
            const r = await api.get("/chat/threads");
            setThreads(r.data || []);
        } catch (_) {
            setThreads([]);
        } finally {
            setLoading(false);
        }
        try {
            const u = await api.get("/chat/unread-count");
            onUnreadRefresh && onUnreadRefresh(u.data?.count || 0);
        } catch (_) {}
    };
    useEffect(() => {
        refresh();
        const iv = setInterval(refresh, 20000);
        return () => clearInterval(iv);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <section className="grid md:grid-cols-[320px_1fr] gap-4" data-testid="admin-chat-panel">
            <div className="sd-card p-4 overflow-y-auto" style={{ maxHeight: 620 }}>
                <div className="flex justify-between items-center mb-3">
                    <div className="overline">Conversations</div>
                    <button onClick={refresh} className="text-xs text-slate-500 hover:text-[#0B192C]">
                        Rafraîchir
                    </button>
                </div>
                {loading && <div className="text-xs text-slate-400">Chargement…</div>}
                {!loading && threads.length === 0 && (
                    <div className="text-xs text-slate-400 py-6 text-center" data-testid="admin-chat-empty">
                        Aucune conversation active. Les échanges commenceront dès qu'un candidat vous écrit.
                    </div>
                )}
                <div className="space-y-1">
                    {threads.map((t) => {
                        const active = selected?.thread_id === t.thread_id;
                        return (
                            <button
                                key={t.thread_id}
                                onClick={() => setSelected(t)}
                                className={`w-full text-left rounded-lg p-3 border transition-colors ${
                                    active
                                        ? "border-[#F5A623] bg-amber-50"
                                        : "border-slate-100 bg-white hover:border-slate-300"
                                }`}
                                data-testid={`admin-chat-thread-${t.thread_id}`}
                            >
                                <div className="flex justify-between items-center gap-2">
                                    <div className="font-semibold text-sm text-[#0B192C] truncate">
                                        {t.candidate_name || "Candidat"}
                                    </div>
                                    {t.unread > 0 && (
                                        <span className="min-w-[20px] h-[20px] px-1.5 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center">
                                            {t.unread}
                                        </span>
                                    )}
                                </div>
                                <div className="text-[11px] text-slate-500 truncate mt-0.5">
                                    {t.last_from_role === "admin" && "Vous : "}
                                    {t.last_message || <i className="text-slate-400">Pièce jointe</i>}
                                </div>
                                <div className="text-[10px] text-slate-400 mt-1">
                                    {new Date(t.last_at).toLocaleString("fr-FR", {
                                        day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit",
                                    })}
                                </div>
                            </button>
                        );
                    })}
                </div>
            </div>
            <div>
                {selected ? (
                    <ChatPanel
                        key={selected.thread_id}
                        threadId={selected.thread_id}
                        myRole="admin"
                        headerLabel={`${selected.candidate_name} · ${selected.candidate_email || ""}`}
                        onUnreadChange={() => refresh()}
                    />
                ) : (
                    <div className="sd-card p-10 text-center text-sm text-slate-400" style={{ minHeight: 300 }}>
                        Sélectionnez une conversation dans la liste.
                    </div>
                )}
            </div>
        </section>
    );
}

function AdminRevenueByCategory() {
    const [data, setData] = useState({ rows: [], total_revenue_ttc: 0 });
    const [loading, setLoading] = useState(true);

    const refresh = async () => {
        setLoading(true);
        try {
            const r = await api.get("/admin/revenue-by-category");
            setData(r.data);
        } catch (_) {
            setData({ rows: [], total_revenue_ttc: 0 });
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { refresh(); }, []);

    const fmt = (v) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(v || 0);
    const max = data.rows.reduce((m, r) => Math.max(m, r.revenue_ttc), 0) || 1;

    return (
        <section className="sd-card p-6" data-testid="admin-revenue-panel">
            <div className="flex justify-between items-start flex-wrap gap-3 mb-5">
                <div>
                    <div className="overline">Analyse commerciale</div>
                    <h2 className="font-display text-xl mt-1">Chiffre d'affaires par métier</h2>
                    <p className="text-xs text-slate-500 mt-1">
                        Uniquement les devis <b>payés</b>. Trie du plus gros CA au plus faible — identifiez les métiers qui vendent le mieux.
                    </p>
                </div>
                <div className="text-right">
                    <div className="text-xs text-slate-500">Total CA payé</div>
                    <div className="font-display text-2xl font-black text-[#0B192C]" data-testid="revenue-total">
                        {fmt(data.total_revenue_ttc)}
                    </div>
                </div>
            </div>
            {loading && <div className="text-sm text-slate-400 py-8 text-center">Chargement…</div>}
            {!loading && data.rows.length === 0 && (
                <div className="text-sm text-slate-400 py-10 text-center" data-testid="revenue-empty">
                    Aucun devis payé pour l'instant — les données apparaîtront ici dès la première vente.
                </div>
            )}
            {!loading && data.rows.length > 0 && (
                <div className="space-y-2">
                    {data.rows.map((r, i) => {
                        const pct = (r.revenue_ttc / max) * 100;
                        const share = data.total_revenue_ttc > 0
                            ? (r.revenue_ttc / data.total_revenue_ttc) * 100
                            : 0;
                        return (
                            <div
                                key={r.category}
                                className="grid grid-cols-[32px_1fr_120px_100px_80px] gap-3 items-center text-sm border-b border-slate-100 pb-2"
                                data-testid={`revenue-row-${r.category}`}
                            >
                                <span className="font-bold text-slate-400 text-xs">#{i + 1}</span>
                                <div>
                                    <div className="font-semibold text-[#0B192C]">{r.label}</div>
                                    <div className="mt-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                                        <div
                                            className="h-2 bg-gradient-to-r from-[#F5A623] to-[#f7b74a] rounded-full"
                                            style={{ width: `${pct}%` }}
                                        />
                                    </div>
                                </div>
                                <div className="text-right">
                                    <div className="font-semibold text-[#0B192C]">{fmt(r.revenue_ttc)}</div>
                                    <div className="text-[10px] text-slate-400">{share.toFixed(1)} % du total</div>
                                </div>
                                <div className="text-right text-xs">
                                    <div className="text-slate-500">Commission</div>
                                    <div className="font-semibold text-emerald-600">{fmt(r.commission)}</div>
                                </div>
                                <div className="text-right text-xs">
                                    <div className="text-slate-500">Devis</div>
                                    <div className="font-semibold">{r.count}</div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </section>
    );
}


function QuoteEditModal({ quote, onClose, onSaved }) {
    const [status, setStatus] = useState(quote.status || "draft");
    const [companyName, setCompanyName] = useState(quote.company_info?.company_name || "");
    const [companyEmail, setCompanyEmail] = useState(quote.company_info?.email || "");
    const [companyPhone, setCompanyPhone] = useState(quote.company_info?.phone || "");
    const [contactName, setContactName] = useState(quote.company_info?.contact_name || "");
    const [missionTitle, setMissionTitle] = useState(quote.mission_input?.title || "");
    const [peopleCount, setPeopleCount] = useState(quote.mission_input?.people_count || 1);
    const [daysCount, setDaysCount] = useState(quote.mission_input?.days_count || 1);
    const [adminNote, setAdminNote] = useState(quote.admin_note || "");
    const [saving, setSaving] = useState(false);

    const save = async () => {
        setSaving(true);
        try {
            await api.patch(`/admin/quotes/${quote.id}`, {
                status,
                admin_note: adminNote,
                company_info: {
                    company_name: companyName,
                    email: companyEmail,
                    phone: companyPhone,
                    contact_name: contactName,
                },
                mission_input: {
                    title: missionTitle,
                    people_count: Number(peopleCount) || 1,
                    days_count: Number(daysCount) || 1,
                },
            });
            toast.success("Devis mis à jour");
            onSaved();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Erreur d'enregistrement");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={onClose}
            data-testid="quote-edit-modal"
        >
            <div
                className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between p-5 border-b border-slate-100">
                    <div>
                        <div className="overline">Édition devis</div>
                        <div className="font-display text-xl">{quote.quote_number}</div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 rounded hover:bg-slate-100 text-slate-500"
                        data-testid="quote-edit-close"
                    >
                        <X size={18} />
                    </button>
                </div>

                <div className="p-5 space-y-4">
                    <div>
                        <label className="block text-xs font-semibold text-slate-500 mb-1">Statut</label>
                        <select
                            value={status}
                            onChange={(e) => setStatus(e.target.value)}
                            className="w-full px-3 py-2 rounded-lg border border-slate-200"
                            data-testid="quote-edit-status"
                        >
                            <option value="draft">Brouillon</option>
                            <option value="sent">Envoyé</option>
                            <option value="accepted">Accepté</option>
                            <option value="paid">Payé</option>
                            <option value="cancelled">Annulé</option>
                            <option value="expired">Expiré</option>
                        </select>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="block text-xs font-semibold text-slate-500 mb-1">Nombre de personnes</label>
                            <input
                                type="number"
                                min={1}
                                value={peopleCount}
                                onChange={(e) => setPeopleCount(e.target.value)}
                                className="w-full px-3 py-2 rounded-lg border border-slate-200"
                                data-testid="quote-edit-people"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-slate-500 mb-1">Nombre de jours</label>
                            <input
                                type="number"
                                min={1}
                                value={daysCount}
                                onChange={(e) => setDaysCount(e.target.value)}
                                className="w-full px-3 py-2 rounded-lg border border-slate-200"
                                data-testid="quote-edit-days"
                            />
                        </div>
                    </div>
                    <p className="text-[11px] text-slate-500 -mt-2">
                        Modifier la mission recalcule automatiquement le total TTC.
                    </p>

                    <div>
                        <label className="block text-xs font-semibold text-slate-500 mb-1">Intitulé mission</label>
                        <input
                            value={missionTitle}
                            onChange={(e) => setMissionTitle(e.target.value)}
                            className="w-full px-3 py-2 rounded-lg border border-slate-200"
                            data-testid="quote-edit-title"
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="block text-xs font-semibold text-slate-500 mb-1">Entreprise</label>
                            <input
                                value={companyName}
                                onChange={(e) => setCompanyName(e.target.value)}
                                className="w-full px-3 py-2 rounded-lg border border-slate-200"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-slate-500 mb-1">Contact</label>
                            <input
                                value={contactName}
                                onChange={(e) => setContactName(e.target.value)}
                                className="w-full px-3 py-2 rounded-lg border border-slate-200"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-slate-500 mb-1">E-mail</label>
                            <input
                                type="email"
                                value={companyEmail}
                                onChange={(e) => setCompanyEmail(e.target.value)}
                                className="w-full px-3 py-2 rounded-lg border border-slate-200"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-slate-500 mb-1">Téléphone</label>
                            <input
                                value={companyPhone}
                                onChange={(e) => setCompanyPhone(e.target.value)}
                                className="w-full px-3 py-2 rounded-lg border border-slate-200"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-xs font-semibold text-slate-500 mb-1">Note interne (admin)</label>
                        <textarea
                            value={adminNote}
                            onChange={(e) => setAdminNote(e.target.value)}
                            rows={3}
                            placeholder="Note visible uniquement en back-office"
                            className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm"
                            data-testid="quote-edit-note"
                        />
                    </div>
                </div>

                <div className="flex justify-end gap-2 p-5 border-t border-slate-100 bg-slate-50/60">
                    <button
                        onClick={onClose}
                        className="btn-ghost-navy text-sm"
                    >
                        Annuler
                    </button>
                    <button
                        onClick={save}
                        disabled={saving}
                        className="btn-navy text-sm disabled:opacity-60"
                        data-testid="quote-edit-save"
                    >
                        {saving ? "Enregistrement…" : "Enregistrer"}
                    </button>
                </div>
            </div>
        </div>
    );
}

